/**
 * ConfirmationStep Component
 * Step 1 of Quick Attendance Wizard - Patient data confirmation
 *
 * @version 2025.1.0
 */

import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  MedicalServices as ExamIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { ConfirmationStepProps } from '../QuickAttendanceWizard.types';
import { formatTime } from '../../../../../types/reception';

/**
 * InfoRow component for displaying labeled information
 */
interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => (
  <Box display="flex" alignItems="center" gap={2} py={1}>
    <Box color="action.active">{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="medium">
        {value}
      </Typography>
    </Box>
  </Box>
);

/**
 * ConfirmationStep - First step of the wizard
 * Displays patient information and requires confirmation
 */
export const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  appointment,
  confirmed,
  onConfirmedChange,
  needsCheckIn,
}) => {
  const formattedTime = formatTime(appointment.hour, appointment.minute);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Confirme os dados do paciente antes de prosseguir:
      </Typography>

      <Divider />

      <Box py={1}>
        <InfoRow
          icon={<PersonIcon />}
          label="Funcionário"
          value={appointment.employeeName}
        />
        {appointment.employeeCpf && (
          <InfoRow
            icon={<BadgeIcon />}
            label="CPF"
            value={appointment.employeeCpf}
          />
        )}
        <InfoRow
          icon={<BusinessIcon />}
          label="Empresa"
          value={appointment.companyName}
        />
        <InfoRow
          icon={<ExamIcon />}
          label="Tipo de Exame"
          value={appointment.examType}
        />
        <InfoRow
          icon={<EventIcon />}
          label="Horário Agendado"
          value={formattedTime}
        />
      </Box>

      <Divider />

      {needsCheckIn && (
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          Ao confirmar, o paciente receberá uma senha e será adicionado à fila de atendimento.
        </Alert>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={confirmed}
            onChange={(e) => onConfirmedChange(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            Confirmo que os dados acima estao corretos
          </Typography>
        }
        sx={{ mt: 1 }}
      />
    </Box>
  );
};

export default ConfirmationStep;
